import { motion, AnimatePresence } from "framer-motion";
import { DotGrid } from "./DotGrid";

// Area A (petrol): top-left, roughly x:70-240, y:55-245
// Area B (red): top-right, roughly x:370-540, y:55-225
// Area C (amber): bottom-center, roughly x:170-430, y:260-340
const POINTS = [
  // Area A points
  { x: 120, y: 90 },
  { x: 180, y: 130 },
  { x: 140, y: 190 },
  { x: 200, y: 200 },
  // Area B points
  { x: 420, y: 100 },
  { x: 480, y: 140 },
  { x: 450, y: 190 },
  { x: 510, y: 170 },
  // Area C points
  { x: 250, y: 295 },
  { x: 330, y: 310 },
  { x: 390, y: 290 },
];

const AREAS = [
  {
    d: "M 80,60 C 70,100 70,190 90,240 L 190,245 C 235,235 250,195 245,145 L 240,85 C 230,50 150,40 110,50 Z",
    fill: "hsl(186 60% 40% / 0.12)",
    stroke: "hsl(186 60% 60% / 0.55)",
  },
  {
    d: "M 380,60 C 368,90 365,160 375,220 L 460,225 C 530,218 548,185 545,140 L 540,85 C 530,50 480,38 430,42 Z",
    fill: "hsl(0 80% 70% / 0.10)",
    stroke: "hsl(0 80% 70% / 0.45)",
  },
  {
    d: "M 200,265 C 190,285 210,335 250,342 L 380,342 C 420,335 440,305 430,275 L 400,260 C 360,250 230,250 210,260 Z",
    fill: "hsl(45 80% 55% / 0.10)",
    stroke: "hsl(45 80% 55% / 0.45)",
  },
];

const DISTANCE_LINES = [
  { from: 0, to: 1, label: "3.1 km" },
  { from: 1, to: 4, label: "6.8 km" },
  { from: 2, to: 8, label: "9.2 km" },
  { from: 5, to: 7, label: "2.4 km" },
];

export const ANALYTICS_DURATIONS = [2500, 2500, 2500, 3000, 2500];

export function AnalyticsAnimation({
  currentStep,
  cycleKey,
}: {
  currentStep: number;
  cycleKey: number;
}) {
  const showPoints = currentStep >= 0;
  const showAreas = currentStep >= 1;
  const showDistances = currentStep >= 2;
  const showHeatmap = currentStep >= 3;
  const showBi = currentStep >= 4;

  return (
    <div className="relative">
      <svg
        viewBox="0 0 620 360"
        className="w-full rounded-xl border border-border/40 bg-white/70"
        style={{ aspectRatio: "620/360" }}
      >
        <DotGrid />

        {/* Heatmap gradient overlay */}
        {showHeatmap && (
          <>
            <defs>
              <radialGradient id="heat1" cx="30%" cy="42%" r="38%">
                <stop offset="0%" stopColor="hsl(0 85% 50%)" stopOpacity="0.45" />
                <stop offset="40%" stopColor="hsl(30 90% 55%)" stopOpacity="0.25" />
                <stop offset="70%" stopColor="hsl(50 85% 55%)" stopOpacity="0.12" />
                <stop offset="100%" stopColor="hsl(120 50% 50%)" stopOpacity="0.03" />
              </radialGradient>
              <radialGradient id="heat2" cx="72%" cy="50%" r="35%">
                <stop offset="0%" stopColor="hsl(0 85% 50%)" stopOpacity="0.40" />
                <stop offset="40%" stopColor="hsl(30 90% 55%)" stopOpacity="0.20" />
                <stop offset="70%" stopColor="hsl(50 85% 55%)" stopOpacity="0.10" />
                <stop offset="100%" stopColor="hsl(120 50% 50%)" stopOpacity="0.02" />
              </radialGradient>
              <radialGradient id="heat3" cx="50%" cy="75%" r="30%">
                <stop offset="0%" stopColor="hsl(30 90% 55%)" stopOpacity="0.35" />
                <stop offset="50%" stopColor="hsl(50 85% 55%)" stopOpacity="0.15" />
                <stop offset="100%" stopColor="hsl(120 50% 50%)" stopOpacity="0.03" />
              </radialGradient>
            </defs>
            <motion.rect
              x="0" y="0" width="620" height="360" rx="12"
              fill="url(#heat1)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2 }}
            />
            <motion.rect
              x="0" y="0" width="620" height="360" rx="12"
              fill="url(#heat2)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.3 }}
            />
            <motion.rect
              x="0" y="0" width="620" height="360" rx="12"
              fill="url(#heat3)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.6 }}
            />
          </>
        )}

        {/* Areas */}
        {showAreas &&
          AREAS.map((area, i) => (
            <motion.path
              key={`area-${i}`}
              d={area.d}
              fill={area.fill}
              stroke={area.stroke}
              strokeWidth={2}
              strokeDasharray="1000"
              initial={{ strokeDashoffset: 1000, fillOpacity: 0 }}
              animate={{ strokeDashoffset: 0, fillOpacity: 1 }}
              transition={{ duration: 1.5, delay: i * 0.3 }}
            />
          ))}

        {/* Distance lines */}
        {showDistances &&
          DISTANCE_LINES.map((line, i) => {
            const from = POINTS[line.from];
            const to = POINTS[line.to];
            const midX = (from.x + to.x) / 2;
            const midY = (from.y + to.y) / 2;
            return (
              <motion.g
                key={`dist-${i}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: i * 0.3 }}
              >
                <line
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                  stroke="hsl(var(--petrol) / 0.4)"
                  strokeWidth={1}
                  strokeDasharray="4 3"
                />
                <rect
                  x={midX - 22}
                  y={midY - 9}
                  width={44}
                  height={18}
                  rx={4}
                  fill="white"
                  stroke="hsl(var(--petrol) / 0.3)"
                  strokeWidth={0.8}
                />
                <text
                  x={midX}
                  y={midY + 4}
                  textAnchor="middle"
                  fontSize="9"
                  fontFamily="Montserrat, system-ui"
                  fontWeight="600"
                  fill="hsl(var(--petrol))"
                >
                  {line.label}
                </text>
              </motion.g>
            );
          })}

        {/* Points */}
        {showPoints &&
          POINTS.map((p, i) => (
            <motion.circle
              key={`${cycleKey}-ap-${i}`}
              cx={p.x}
              cy={p.y}
              r={6}
              fill="hsl(186 60% 40%)"
              stroke="hsl(186 60% 30%)"
              strokeWidth={1.5}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, delay: i * 0.12 }}
            />
          ))}
      </svg>

      {/* BI card */}
      <AnimatePresence>
        {showBi && (
          <motion.div
            className="absolute right-2 top-4 bg-white/95 border border-border rounded-lg shadow-lg p-3 w-44"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 rounded bg-petrol/10 flex items-center justify-center">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--petrol))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18" />
                  <path d="M18 17V9" />
                  <path d="M13 17V5" />
                  <path d="M8 17v-3" />
                </svg>
              </div>
              <span className="text-xs font-heading font-semibold text-foreground">BI-integration</span>
            </div>
            <div className="space-y-1.5 text-[11px] font-body">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                <span className="text-muted-foreground">6 datapunkter synkade</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                <span className="text-muted-foreground">2 analysområden</span>
              </div>
              <div className="text-[10px] text-muted-foreground/70 mt-1 pt-1 border-t border-border">
                Delad data i realtid
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
